import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { TbCaretDownFilled, TbChevronRight } from 'react-icons/tb'
import clsx from 'clsx'
import type { StackProps } from '~/components-v2'
import { Stack } from '~/components-v2'
import { useClickAnyWhere } from 'usehooks-ts'
import { JSX } from 'react'

type DisclosureContentProps = Pick<
  StackProps,
  'className' | 'gap' | 'row' | 'wrap'
>

type DisclosureProps = {
  className?: string
  children: ReactNode
  megaMenu?: boolean
  title: string | JSX.Element
  contentProps?: DisclosureContentProps
}

export const Disclosure = ({
  className,
  title,
  children,
  megaMenu,
  contentProps = { gap: 0 },
}: DisclosureProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDetailsElement>(null)
  const summaryRef = useRef<HTMLElement>(null)

  useClickAnyWhere((event) => {
    if (!isOpen) return
    if (!ref.current || !summaryRef.current) return
    if (summaryRef.current.contains(event.target as Node)) return
    setIsOpen(false)
  })

  useEffect(() => {
    const main = document.getElementById('main-content')
    const detailsEl = ref.current

    if (!main || !detailsEl) return

    const isNested =
      detailsEl.closest('.nav-sub-column, .mobile-nav-subcolumn') !== null
    if (isNested) return

    if (isOpen) {
      detailsEl.setAttribute('data-disclosure-open', 'true')
    } else {
      detailsEl.removeAttribute('data-disclosure-open')
    }

    const checkIfAnyOpen = () => {
      const anyOpen =
        document.querySelectorAll('[data-disclosure-open="true"]').length > 0
      main.classList.toggle('blur', anyOpen)
    }

    checkIfAnyOpen()

    return () => {
      detailsEl.removeAttribute('data-disclosure-open')
      checkIfAnyOpen()
    }
  }, [isOpen])

  return (
    <details
      ref={ref}
      open={isOpen}
      className={clsx(className, 'disclosure', megaMenu && 'mega-menu')}
    >
      <summary
        ref={summaryRef}
        onClick={(e) => {
          e.preventDefault()
          setIsOpen((prev) => !prev)
        }}
      >
        <span className="disclosure-label">{title}</span>
        <TbCaretDownFilled aria-hidden="true" className="desktop" />
        <TbChevronRight aria-hidden="true" className="mobile" />
      </summary>
      <Stack
        {...contentProps}
        row
        gap={0}
        className={clsx('disclosure-content', contentProps.className)}
      >
        {children}
      </Stack>
    </details>
  )
}
